<template>
    <div class="container">
        <!-- 🇵🇭 1. HIGH-PRIORITY NATIONAL HOLIDAY ACCENT ALERT -->
        <v-alert
            v-for="(holiday, idx) in todayHolidays"
            :key="'holiday-' + idx"
            :value="true"
            :icon="getHolidayIcon(holiday.id)"
            :class="['rounded-4 holiday-priority-card', getHolidayThemeClass(holiday.id)]"
            outline
        >
            <div class="holiday-content-block">
                <strong class="holiday-title-text d-block mb-1">{{ holiday.greeting }}</strong>
                <div class="details-section holiday-desc-text">
                    {{ holiday.description }}
                </div>
            </div>
        </v-alert>

        <!-- public notifications -->
        <v-alert
            v-for="(item, index) in filteredNotifications"
                :key="index"
                :value="true"
                color="primary"
                :icon="item.icon"
                outline
                class="rounded-4"
        >
            <div @click="showDetails(item.detailsLink)" :class="item.detailsLink ? 'header-clickable' : ''" :title="item.detailsLink ? 'Click to view details' : ''">
                <strong>{{ item.typeName }}</strong>
                <span v-if="item.detailsLink" class="mr-5"><v-icon small>open_in_new</v-icon></span>
            </div>
            <div v-if="item.displayDuration" class="details-section caption">
                <b>{{ formatSchedule(item) }}</b>
            </div>
            <div class="details-section" v-html="item.details"></div>
        </v-alert>
     </div>
</template>

<script>
import publicNotificationsApi from '@/api/publicnotification'
import publicPages from '@/helpers/public-pages.js'
import { hasEditRights } from '@/helpers/access-permissions'
import { parseToManilaTime, formatWithLocalTimezone } from '@/helpers/date-helpers'
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'

export default {
    data() {
        return {
            notifications: [],
            error: false,
            errorMessage: null,
            signalRConnection: null
        }
    },
    methods: {
        showDetails(link) {
            if (link) window.open(link)
        },
        async getNowShowing() {
            try {
                const cachedData = localStorage.getItem('publicNotifications')
                if (cachedData) {
                    this.notifications = JSON.parse(cachedData)
                }

                const response = await publicNotificationsApi.getNowShowing()
                if (response && response.status === 200 && response.data.nowShowing) {
                    this.notifications = response.data.nowShowing
                    localStorage.setItem('publicNotifications', JSON.stringify(this.notifications))
                    this.$emit('notifications-count', this.notificationsCount)
                }
            } catch (error) {
                this.error = true
                this.errorMessage = error.message || error
            }
        },
        
        /**
         * 1. INTEGRATED TIMEZONE HELPERS
         * Reuses parseToManilaTime and formatWithLocalTimezone to avoid duplicate format code.
         */
        formatSchedule(data) {
            // 1. Force strings into +08:00 offset configuration using your shared helper
            const start = parseToManilaTime(data.effectiveDate);
            const end = parseToManilaTime(data.expirationDate);
            const today = this.$moment();

            // 2. Generate Calendar day labels using standard moment
            const startDateLabel = today.isSame(start, 'day')
                ? `Today (${start.format('ddd')})`
                : start.format('MMM D (ddd)');

            const endDateLabel = today.isSame(end, 'day')
                ? `Today (${end.format('ddd')})`
                : end.format('MMM D (ddd)');

            // 3. Format the times natively with their local timezone labels appended
            const formattedStart = formatWithLocalTimezone(start, 'h:mm A'); // e.g., "11:30 AM Manila Time"
            const formattedEnd = formatWithLocalTimezone(end, 'h:mm A');     // e.g., "1:30 PM Manila Time"

            // 4. Output matching layout rules
            if (start.isSame(end, 'day')) {
                // If it's the same day, output: "Today (Sat) from 11:30 AM to 1:30 PM Manila Time"
                return `${startDateLabel} from ${start.format('h:mm A')} to ${formattedEnd}`;
            }

            // If it spans across different days, output: "Jun 20 (Sat) 11:30 AM Manila Time to Jun 21 (Sun) 1:30 PM Manila Time"
            return `${startDateLabel} ${formattedStart} to ${endDateLabel} ${formattedEnd}`;
        },

        /**
         * 2. SIGNALR REAL-TIME PUBLIC REFRESH
         * Listens to the same hub. When an emergency or new notice pushes, it automatically
         * refreshes the page list without requiring manual window reloads.
         */
        async initializeSignalR() {
            try {
                this.signalRConnection = new HubConnectionBuilder()
                    .withUrl(`${process.env.VUE_APP_HUB_URL}maintenance`)
                    .configureLogging(LogLevel.Information)
                    .withAutomaticReconnect()
                    .build()

                this.signalRConnection.onreconnected(async () => {
                    await this.subscribeToGroup()
                })

                await this.signalRConnection.start()
                await this.subscribeToGroup()
                this.listenForAlerts()

            } catch (error) {
                console.error('Public View SignalR Initialization Failed:', error)
            }
        },
        async subscribeToGroup() {
            if (!this.signalRConnection) return
            await this.signalRConnection.invoke(
                process.env.VUE_APP_HUB_MAINTENANCE_INVOKE, 
                process.env.VUE_APP_HUB_MAINTENANCE_GROUP
            ).catch(err => console.error('Failed to subscribe public view:', err))
        },
        listenForAlerts() {
            if (!this.signalRConnection) return

            this.signalRConnection.on(process.env.VUE_APP_HUB_MAINTENANCE_ALERT, (data) => {
                console.log('SignalR Push received on Public View. Refreshing notice grid...')
                
                // When an active admin upserts notice models, pull the updated payload instantly
                this.getNowShowing()
            })
        },

        // public holidays
        getHolidayThemeClass(id) {
            // Separates festive cultural greetings from local historical remembrance milestones
            const festiveIds = ['eid-fitr', 'eid-adha', 'independence'];
            return festiveIds.includes(id) ? 'theme-festive-cultural' : 'theme-solemn-historical';
        },
        getHolidayIcon(id) {
            const iconMap = {
                'eid-fitr': 'brightness_3',
                'eid-adha': 'star_rate',
                'holyweek': 'church',
                'valor': 'military_tech',
                'independence': 'flag',
                'rizalday': 'history_edu',
                'bonifacio': 'gavel'
            };
            return iconMap[id] || 'event';
        }
    },
    created() {
        this.getNowShowing()
    },
    async mounted() {
        await this.initializeSignalR()
    },
    beforeDestroy() {
        if (this.signalRConnection) {
            this.signalRConnection.stop()
            this.signalRConnection = null
        }
    },
    watch: {
        // Listens closely to the final computed count and alerts the parent navbar wrapper component
        notificationsCount: {
            immediate: true, // Executes immediately upon layout creation
            handler(newCount) {
                this.$emit('notifications-count', newCount);
            }
        }
    },
    computed: {
        filteredNotifications() {
            return this.notifications.filter(item => {
                if (item.recipient === 'All Users') return true
                if (item.recipient === 'HR Admin') return this.isHRAdmin
                if (item.recipient === 'Compensation Admin') return this.isCompensationAdmin
                if (item.recipient === 'Payroll Admin') return this.isPayrollAdmin
                return false
            })
        },
        isHRAdmin() {
            const routerEmployees = this.$router.resolve({ name: 'Employees' })
            const routerHolidays = this.$router.resolve({ name: 'HolidaysMain' })
            const routerCalendarPeriods = this.$router.resolve({ name: 'CalendarPeriods' })

            return hasEditRights(routerEmployees.route.fullPath) ||
                    hasEditRights(routerHolidays.route.fullPath) ||
                    hasEditRights(routerCalendarPeriods.route.fullPath)
        },
        isCompensationAdmin() {
             const routerCompBen = this.$router.resolve({ name: 'AllocationMain' })
             return hasEditRights(routerCompBen.route.fullPath)
        },
        isPayrollAdmin() {
            const routerPayroll = this.$router.resolve({ name: 'PayrollGenerate' })
            return hasEditRights(routerPayroll.route.fullPath)
        },

        // Future updates - pull public holidays from API (DB->Cache)
        // Regular and Special Non-Working Holidays Only
        todayHolidays() {
            const today = new Date()
            const m = today.getMonth()
            const d = today.getDate()

            const ALL_PUBLIC_HOLIDAYS = [
                { 
                    id: 'birthday', 
                    priority: 100, // Higher priority numerical parameter
                    match: (m, d) => {
                        const birthDateStr = this.$store.state.birthDate; 
                        if (!birthDateStr) return false;
                        
                        const birthDate = new Date(birthDateStr);
                        if (isNaN(birthDate.getTime())) return false; 
                        
                        return birthDate.getMonth() === m && birthDate.getDate() === d;
                    },
                    greeting: "🎂 Happy Birthday!", 
                    description: "Wishing you a wonderful day filled with joy, celebration, and success. Happy Birthday from the SweldoNow Team!" 
                },
                { 
                    id: 'newyear', 
                    priority: 10, 
                    match: (m, d) => (m === 11 && d === 31) || (m === 0 && d <= 1), 
                    greeting: "🎆 Happy New Year!", 
                    description: "Cheers to fresh beginnings! May the upcoming year bring you personal happiness, professional growth, and everything you hope to achieve." 
                }, 
                { 
                    id: 'valentine', 
                    priority: 10, 
                    match: (m, d) => m === 1 && d === 14, 
                    greeting: "💖 Happy Valentine’s Day!", 
                    description: "Today is all about celebrating care and appreciation. Thank you for your amazing dedication and for being such a valued part of our team!" 
                }, 
                { 
                    id: 'lunar', 
                    priority: 10, 
                    match: (m, d) => m === 1 && d === 6, 
                    greeting: "🧧 Happy Lunar New Year!", 
                    description: "Wishing you a year filled with abundance, excellent health, and dynamic good fortune. Gong Xi Fa Cai!" 
                }, 
                { 
                    id: 'eid-fitr', 
                    priority: 10, 
                    match: (m, d) => m === 2 && d === 20, 
                    greeting: "☪ Eid al-Fitr Mubarak", 
                    description: "Wishing a blessed and joyful feast to our Muslim team members as you conclude the holy month of Ramadan. May this day bring you and your family peace and unity." 
                }, 
                { 
                    id: 'holyweek', 
                    priority: 10, 
                    match: (m, d) => m === 3 && d >= 1 && d <= 5, 
                    greeting: "✝️ Holy Week Observance", 
                    description: "Wishing you a peaceful and meaningful Holy Week. May this solemn season bring you and your loved ones a time of quiet reflection, comfort, and restful renewal." 
                },
                { 
                    id: 'valor', 
                    priority: 10, 
                    match: (m, d) => m === 3 && d === 9, 
                    greeting: "🇵🇭 Day of Valor (Araw ng Kagitingan)", 
                    description: "Honoring the supreme sacrifices of our gallant veterans. Their extraordinary heroism remains an enduring symbol of Filipino courage." 
                }, 
                { 
                    id: 'laborday', 
                    priority: 10, 
                    match: (m, d) => m === 4 && d === 1, 
                    greeting: "💼 Happy Labor Day!", 
                    description: "Saluting your incredible dedication, hard work, and the vital energy you bring to our team every day. Thank you for everything you do!" 
                }, 
                { 
                    id: 'eid-adha', 
                    priority: 10, 
                    match: (m, d) => m === 4 && d === 27, 
                    greeting: "☪ Eid al-Adha Mubarak", 
                    description: "Joining our Muslim colleagues in celebrating the Feast of Sacrifice, honoring devotion, selflessness, and charity. May your day be filled with blessings." 
                }, 
                { 
                    id: 'independence', 
                    priority: 10, 
                    match: (m, d) => m === 5 && d === 12, 
                    greeting: "🇵🇭 Philippine Independence Day", 
                    description: "Celebrating national sovereignty and honoring the heroes who fought for liberty. Let us continue to build a progressive nation together." 
                }, 
                { 
                    id: 'ninoy', 
                    priority: 10, 
                    match: (m, d) => m === 7 && d === 21, 
                    greeting: "🕊 Ninoy Aquino Day", 
                    description: "Commemorating the heroism of Senator Benigno 'Ninoy' Aquino Jr., whose martyrdom ignited the path toward freedom and democracy." 
                }, 
                { 
                    id: 'heroesday', 
                    priority: 10, 
                    match: (m, d) => m === 7 && d === 31, 
                    greeting: "🦸 National Heroes Day", 
                    description: "Paying tribute to all heroes of our nation—both historical icons and everyday modern heroes whose dedication uplifts our communities." 
                }, 
                { 
                    id: 'halloween', 
                    priority: 10, 
                    match: (m, d) => m === 9 && (d === 30 || d === 31), 
                    greeting: "🎃 Happy Halloween!", 
                    description: "Wishing you a fun, safe, and fantastic Halloween celebration today!" 
                }, 
                { 
                    id: 'allsaints', 
                    priority: 10, 
                    match: (m, d) => m === 10 && d >= 1 && d <= 2, 
                    greeting: "🕯 All Saints’ & All Souls' Day", 
                    description: "Remembering and honoring our departed loved ones with prayers and quiet reflection. May their cherished memories bring comforting peace." 
                }, 
                { 
                    id: 'bonifacio', 
                    priority: 10, 
                    match: (m, d) => m === 10 && d === 30, 
                    greeting: "🇵🇭 Bonifacio Day", 
                    description: "Celebrating the birth anniversary of the Great Plebeian, Andres Bonifacio. We honor his deep love for country and revolutionary leadership." 
                }, 
                { 
                    id: 'immaculate', 
                    priority: 10, 
                    match: (m, d) => m === 11 && d === 8, 
                    greeting: "🙏 Feast of the Immaculate Conception", 
                    description: "Joining the community in celebrating this special day of solemn obligation. May it bring peace, grace, and hope to your homes." 
                }, 
                { 
                    id: 'christmas', 
                    priority: 10, 
                    match: (m, d) => m === 11 && d >= 24 && d <= 25, 
                    greeting: "🎄 Merry Christmas!", 
                    description: "Wishing you and your family a beautiful Christmas season filled with warmth, love, peace, and shared joy. Merry Christmas!" 
                }, 
                { 
                    id: 'rizalday', 
                    priority: 10, 
                    match: (m, d) => m === 11 && d === 30, 
                    greeting: "🇵🇭 Rizal Day", 
                    description: "Remembering the martyrdom and monumental legacy of our National Hero, Dr. José Rizal. May his patriotism continue to inspire us." 
                }
            ]

            const matches = ALL_PUBLIC_HOLIDAYS.filter(h => h.match(m, d))
            
            // Sort array: ensures 'birthday' (priority 100) always sorts above regular holidays (priority 10)
            return matches.sort((a, b) => b.priority - a.priority);
        },

        notificationsCount() {
            const lastRead = localStorage.getItem('publicNotificationsLastRead') || 0;
            const holidayLastSeenDate = localStorage.getItem('holidayLastReadDate'); // Format: YYYY-MM-DD
            const todayStr = this.$moment().format('YYYY-MM-DD');

            // 1. Filter database notifications using your existing moment logic
            const newDatabaseNotifications = this.notifications.filter(notif => {
                const notifTime = this.$moment(notif.createdOn).valueOf(); 
                return notifTime > Number(lastRead);
            });
            const databaseCount = newDatabaseNotifications.length;
            
            // 2. Filter holidays: Only count if there is a matching holiday today AND the user hasn't seen today's holiday yet
            let holidayCount = 0;
            if (this.todayHolidays && this.todayHolidays.length > 0) {
                if (holidayLastSeenDate !== todayStr) {
                    holidayCount = this.todayHolidays.length;
                }
            }
            
            return holidayCount + databaseCount;
        }
    },
}
</script>

<style scoped>
/* Keeping your existing styles exactly as they are */
.container {
    width: 100%; 
    height: 100%; 
}
.header-clickable {
    text-decoration: underline;
    cursor: pointer;
}
.details-section {
    color: #3b3b3b;
}
</style>
